import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { FolderGrantRow } from "@/lib/folder-access";

/**
 * Tildelinger gruppe ↔ mappe i dokumentarkivet (VIB-2721 / VIB-2726).
 *
 * Den arkivspesifikke halvdelen av gruppemodellen: gruppene selv bor i
 * `use-access-groups.ts` og vet ingenting om mapper.
 *
 * Tilgangsnivået er to boolske felt i basen (`can_read`, `can_write`) med en
 * CHECK som krever minst én av dem. I UI-et er det ett valg av tre —
 * ingen / lese / skrive — fordi en tildeling som gir skrive uten lese ikke
 * finnes som meningsfull tilstand.
 */
export type GrantLevel = "none" | "read" | "write";

export interface DocumentFolderGrant extends FolderGrantRow {
  granted_at: string;
  granted_by: string | null;
}

export function grantLevelOf(
  grant: Pick<FolderGrantRow, "can_read" | "can_write"> | undefined,
): GrantLevel {
  if (!grant) return "none";
  return grant.can_write ? "write" : "read";
}

export const GRANT_LEVEL_LABELS: Record<GrantLevel, string> = {
  none: "Ingen tilgang",
  read: "Kan lese",
  write: "Kan lese og endre",
};

/**
 * ALLE tildelinger, ikke én mappe.
 *
 * Tilgang til en undermappe avhenger av HELE kjeden oppover, så en
 * per-mappe-spørring ville måttet gjøres én gang per nivå for å svare på «hvem
 * har tilgang her». Tabellen har én rad per mappe × gruppe og er liten nok til
 * å hentes i sin helhet. RLS filtrerer uansett bort mapper kalleren ikke kan
 * lese (`document_folder_grants_select`).
 */
export function useDocumentFolderGrants() {
  return useQuery({
    queryKey: ["document-folder-grants"],
    queryFn: async (): Promise<DocumentFolderGrant[]> => {
      const { data, error } = await supabase
        .from("document_folder_grants")
        .select("folder_id, group_id, can_read, can_write, granted_at, granted_by");
      if (error) throw error;
      return (data ?? []) as DocumentFolderGrant[];
    },
  });
}

function forklar(e: Error): string {
  return /row-level security/i.test(e.message)
    ? "Bare admin kan endre hvem en mappe er delt med."
    : e.message;
}

/** Setter nivået for én gruppe på én mappe. `none` fjerner tildelingen. */
export function useSetDocumentFolderGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      folderId: string;
      groupId: string;
      level: GrantLevel;
      grantedBy: string;
    }) => {
      if (input.level === "none") {
        const { error } = await supabase
          .from("document_folder_grants")
          .delete()
          .eq("folder_id", input.folderId)
          .eq("group_id", input.groupId);
        if (error) throw error;
        return;
      }

      // Skrive impliserer lese: ellers kunne en gruppe lagt inn innhold den
      // ikke får se igjen, og `can_write_document_folder` krever lesetilgang
      // i hele kjeden uansett.
      const { error } = await supabase.from("document_folder_grants").upsert(
        {
          folder_id: input.folderId,
          group_id: input.groupId,
          can_read: true,
          can_write: input.level === "write",
          granted_by: input.grantedBy,
        },
        { onConflict: "folder_id,group_id" },
      );
      if (error) throw error;
    },
    onSuccess: (_d, input) =>
      toast.success(
        input.level === "none" ? "Tilgangen er fjernet" : "Tilgangen er lagret",
      ),
    onError: (e: Error) => toast.error(forklar(e)),
    onSettled: async () => {
      // Bevisst bred: en tildeling kan gjøre en hel mappe (og alle
      // undermappene) synlig eller usynlig for hvem som helst, og
      // `document-folder-grants` er ÉN felles liste — en ID-rettet
      // invalidering ville ikke truffet den.
      await invalidateFor(qc, "document_folder_grants");
    },
  });
}
